import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import axios from "axios";
import { QUESTIONS, computeResult, STAGE_META } from "./data";

const API = process.env.REACT_APP_BACKEND_URL;
const EYEBROW = "text-[11px] tracking-[0.3em] uppercase font-semibold text-[#8A6A2E]";

export default function Assessment() {
  const [step, setStep] = useState(0); // 0..14 questions, 15=lead-capture, 16=result
  const [answers, setAnswers] = useState({});
  const [lead, setLead] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const total = QUESTIONS.length;
  const q = step < total ? QUESTIONS[step] : null;
  const pct = Math.round(((step + 1) / (total + 1)) * 100);

  const result = useMemo(() => computeResult(answers), [answers]);
  const stageInfo = STAGE_META[result.stage];

  const pick = (idx) => {
    setAnswers((a) => ({ ...a, [q.id]: idx }));
    setTimeout(() => setStep((s) => s + 1), 220);
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await axios.post(`${API}/api/leads`, {
        name: lead.name,
        email: lead.email,
        phone: lead.phone || null,
        topic: `Confidence Assessment · ${stageInfo.name}`,
        message: `Confidence score: ${result.confidence}/100.\nRecommended stage: ${result.stage} · ${stageInfo.name}.\n\nAnswers:\n${Object.entries(answers)
          .map(([qid, idx]) => {
            const qq = QUESTIONS.find((x) => x.id === qid);
            return `• ${qq.text}\n   → ${qq.options[idx].label}`;
          })
          .join("\n")}`,
      });
      setStep(total + 1);
    } catch (ex) {
      const d = ex.response?.status < 500 ? ex.response?.data?.detail : null;
      setErr(typeof d === "string" ? d : "Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5EF] text-[#0F1B2D]" data-testid="assessment-page">
      <header className="bg-white/90 backdrop-blur-xl border-b border-[#E6E2D8] sticky top-0 z-20">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="assessment-home-link">
            <img src="/prospera-icon.png" alt="" className="w-8 h-8 rounded-full" />
            <span className="font-display text-[#0B1F3F] text-lg">Prospera</span>
          </Link>
          <Link to="/" className="text-[#5A6476] text-sm hover:text-[#0B1F3F] inline-flex items-center gap-1" data-testid="assessment-back-link">
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
        <div className="h-[3px] bg-[#EDE9DF]">
          <div
            className="h-full bg-[#B08D57] transition-[width] duration-500"
            style={{ width: `${pct}%` }}
            data-testid="assessment-progress"
          />
        </div>
      </header>

      <main className="max-w-[760px] mx-auto px-6 lg:px-12 py-14 md:py-20">
        {step < total && q && (
          <div data-testid={`assessment-q-${step + 1}`}>
            <div className={EYEBROW}>Question {step + 1} of {total}</div>
            <h1 className="font-display text-[#0B1F3F] text-3xl sm:text-4xl mt-4 leading-tight">
              {q.text}
            </h1>
            <ul className="mt-10 space-y-3">
              {q.options.map((o, i) => (
                <li key={o.label}>
                  <OptionButton
                    label={o.label}
                    active={answers[q.id] === i}
                    onClick={() => pick(i)}
                    testid={`opt-${step + 1}-${i}`}
                  />
                </li>
              ))}
            </ul>
            {step > 0 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="mt-8 text-[#5A6476] hover:text-[#0B1F3F] text-sm inline-flex items-center gap-2"
                data-testid="prev-btn"
              >
                <ArrowLeft size={14} /> Previous
              </button>
            )}
          </div>
        )}

        {step === total && (
          <div data-testid="assessment-capture">
            <div className={EYEBROW}>Your score is ready</div>
            <h1 className="font-display text-[#0B1F3F] text-3xl sm:text-4xl mt-4 leading-tight">
              Where should we send your personalised Confidence Score?
            </h1>
            <p className="text-[#5A6476] mt-4 max-w-xl leading-relaxed">
              A one-page result with your score, your recommended stage, and a
              short read on what to do next. No pressure, no follow-up spam.
              We contact you once, only if you'd like.
            </p>
            <form onSubmit={submitLead} className="mt-8 space-y-4 max-w-lg">
              <Field label="Full name" value={lead.name} onChange={(e) => setLead((l) => ({ ...l, name: e.target.value }))} required testid="lead-name" />
              <Field label="Email" type="email" value={lead.email} onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))} required testid="lead-email" />
              <Field label="Phone (optional)" type="tel" value={lead.phone} onChange={(e) => setLead((l) => ({ ...l, phone: e.target.value }))} testid="lead-phone" />
              {err && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5" data-testid="lead-error">
                  {err}
                </div>
              )}
              <button type="submit" disabled={busy} data-testid="reveal-score" className="btn-primary w-full">
                {busy ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Preparing your score…
                  </>
                ) : (
                  "Reveal my Confidence Score"
                )}
              </button>
              <p className="text-xs text-[#8C93A1] max-w-md">
                By continuing you agree to our{" "}
                <Link to="/legal/privacy" className="underline">Privacy Policy</Link>.
              </p>
            </form>
          </div>
        )}

        {step > total && <Result result={result} stageInfo={stageInfo} />}
      </main>
    </div>
  );
}

function OptionButton({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left px-5 py-4 rounded-2xl border flex items-center justify-between gap-4 transition-[border-color,background-color,box-shadow] duration-200 ${
        active
          ? "border-[#0B1F3F] bg-white shadow-[0_10px_30px_-15px_rgba(11,31,63,0.35)]"
          : "border-[#E6E2D8] bg-white hover:border-[#B08D57]"
      }`}
    >
      <span className="text-[15px] text-[#0F1B2D]">{label}</span>
      <span
        className={`w-6 h-6 shrink-0 rounded-full border flex items-center justify-center transition-colors ${
          active ? "border-[#0B1F3F] bg-[#0B1F3F] text-white" : "border-[#CFC9BB]"
        }`}
      >
        {active && <Check size={14} />}
      </span>
    </button>
  );
}

function Result({ result, stageInfo }) {
  return (
    <div data-testid="assessment-result">
      <div className={EYEBROW}>Your Confidence Assessment</div>
      <h1 className="font-display text-[#0B1F3F] text-4xl sm:text-5xl mt-4">Confidence Score</h1>
      <div className="mt-8 flex items-baseline gap-3">
        <span className="font-display text-[#0B1F3F] text-7xl sm:text-8xl tabular-nums" data-testid="score-value">
          {result.confidence}
        </span>
        <span className="text-[#5A6476] text-lg">/ 100</span>
      </div>
      <div className="h-2 mt-6 rounded-full bg-[#EDE9DF] overflow-hidden max-w-2xl">
        <div className="h-full bg-[#B08D57]" style={{ width: `${result.confidence}%` }} />
      </div>

      <div className="mt-12 rounded-2xl bg-white border border-[#E6E2D8] p-8 max-w-3xl" data-testid="result-stage">
        <div className={EYEBROW}>Recommended stage</div>
        <h2 className="font-display text-[#0B1F3F] text-2xl mt-3">{stageInfo.name}</h2>
        <div className="text-[#8A6A2E] text-sm mt-1">{stageInfo.tagline}</div>
        <p className="text-[#0F1B2D] text-[15px] leading-relaxed mt-5">{stageInfo.body}</p>
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-5">
        <Link to="/#contact" className="btn-primary" data-testid="result-book">
          Book my Confidence Review
        </Link>
        <Link to="/" className="btn-secondary" data-testid="result-home">
          Back to Prospera
        </Link>
      </div>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <label className="block">
      <span className="text-[12px] font-semibold text-[#0B1F3F]">{label}</span>
      <input
        {...rest}
        data-testid={testid}
        className="mt-2 w-full px-4 py-3 rounded-xl bg-white border border-[#E6E2D8] text-[#0F1B2D] placeholder-[#A3A9B5] text-[15px] focus:outline-none focus:border-[#0B1F3F] focus:ring-4 focus:ring-[#0B1F3F]/5 transition-[border-color,box-shadow]"
      />
    </label>
  );
}
